import dayjs from 'dayjs';
import type {Timezone} from '@gl/lit/types/timezone.js';
import type {
  DateInputString,
  HourAndMinuteString,
  ISOString,
  Nullable,
} from '@gl/lit/types/general.js';
import type {AudienceScheduleStartTypeEnum} from '@gl/lit/types/journey/audience_frequency.js';
import {
  AudienceFrequencyEnum,
  AudienceScheduleEndTypeEnum,
  isAudienceFrequency,
} from '@gl/lit/types/journey/audience_frequency.js';
import type {AudienceScheduleOccurrence} from '@gl/lit/types/journey/schedule.js';
import {ByType} from '@gl/lit/types/journey/schedule.js';
import {WeekdaysEnum} from '@gl/lit/common/constants/dates.js';
import {convertToISOString} from '@gl/lit/utils/dates.js';
import {isNumber, isString} from '@gl/lit/utils/index.js';
import type FlyAudienceExportSchedule from './index.js';
import type {AudienceExportSchedule} from '@gl/lit/types/audience_exports.js';

export function getDefaultStartDate(): ISOString {
  return convertToISOString(dayjs().add(30, 'minutes'));
}

export function getDefaultEndDate(
  startDate?: Nullable<DateInputString>,
  startTime?: Nullable<HourAndMinuteString>
): ISOString {
  let date = dayjs(startDate ?? getDefaultStartDate()).add(1, 'month');
  if (startTime) {
    const [hour, minutes] = startTime.split(':');
    date = date
      .set('hours', Number.parseInt(hour, 10))
      .set('minutes', Number.parseInt(minutes, 10));
  }
  return convertToISOString(date);
}

export function getDefaultOneTimeState(startState: {
  startType: AudienceScheduleStartTypeEnum;
  startDate: Nullable<DateInputString>;
  startTime: Nullable<HourAndMinuteString>;
  startTimezone: Nullable<Timezone>;
}) {
  return {
    ...startState,
    endType: null,
    endDate: null,
    endTime: null,
    endTimezone: null,
    frequency: null,
    occurrence: null,
  };
}

export function getDefaultOngoingState(startState: {
  startType: AudienceScheduleStartTypeEnum;
  startDate: Nullable<DateInputString>;
  startTime: Nullable<HourAndMinuteString>;
  startTimezone: Nullable<Timezone>;
}) {
  return {
    ...startState,
    endType: AudienceScheduleEndTypeEnum.NEVER,
    endDate: null,
    endTime: null,
    endTimezone: null,
    frequency: AudienceFrequencyEnum.DAILY,
    occurrence: [
      {
        type: ByType.WEEK_DAY,
        values: [
          WeekdaysEnum.MONDAY,
          WeekdaysEnum.TUESDAY,
          WeekdaysEnum.WEDNESDAY,
          WeekdaysEnum.THURSDAY,
          WeekdaysEnum.FRIDAY,
        ],
      },
    ] as AudienceScheduleOccurrence[],
  };
}

export function dispatchChangeEvent(host: FlyAudienceExportSchedule): void {
  if (!host.isConnected) return;
  const {
    campaignType,
    frequency,
    startType,
    startDate,
    startTime,
    startTimezone,
    endType,
    endDate,
    endTime,
    endTimezone,
    occurrence,
  } = host.model;

  const freq = frequencyAsType(frequency ?? '');

  host.dispatchEvent(
    new CustomEvent<AudienceExportSchedule>('export-schedule:change', {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: {
        schedule_type: campaignType,
        frequency: freq ? (freq as AudienceFrequencyEnum) : null,
        start_type: startType,
        start_time:
          startDate && startTime && startTimezone
            ? {
                time: `${startDate}T${startTime}`,
                timezone: startTimezone.name,
              }
            : null,
        end_type: endType,
        end_time:
          endDate && endTime && endTimezone
            ? {
                time: `${endDate}T${endTime}`,
                timezone: endTimezone.name,
              }
            : null,
        occurrence:
          occurrence?.map(occ => {
            return {
              type: occ.type,
              values: occ.values.map(Number),
              bySetPos: occ.bySetPos ?? null,
            };
          }) ?? [],
      },
    })
  );
}

export function isFullDateString(value: unknown): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d$/;
  if (!isString(value)) return false;
  return regex.test(value);
}

export function frequencyAsType(frequency?: string): string | '' {
  if (!isAudienceFrequency(frequency)) return '' as const;
  return frequency?.split(' ').join('_');
}

export function normalizeFrequency(
  frequency?: string
): AudienceFrequencyEnum | '' {
  if (!frequency) return '';
  const value = frequency.split('_').join(' ');
  return isAudienceFrequency(value) ? value : '';
}

export function isValidoccurrenceEntry(occurrence: AudienceScheduleOccurrence) {
  switch (occurrence.type) {
    case ByType.MONTH_DAY:
      return {
        type: ByType.MONTH_DAY,
        values: occurrence.values.filter(isValidoccurrenceMonthDay),
        bySetPos: occurrence.bySetPos,
      };
    case ByType.WEEK_DAY:
      return {
        type: ByType.MONTH_DAY,
        values: occurrence.values.filter(isValidoccurrenceWeekDay),
        bySetPos: occurrence.bySetPos,
      };
    default:
      return false;
  }
}

export const MONTH_MIN_DAY = 1;
export const MONTH_MAX_DAY = 31;
export function isValidoccurrenceMonthDay(value: number) {
  return value >= MONTH_MIN_DAY && value <= MONTH_MAX_DAY;
}

export const MIN_WEEKDAY = 0;
export const MAX_WEEKDAY = 6;
export function isValidoccurrenceWeekDay(value: number) {
  return isNumber(value) && value >= MIN_WEEKDAY && value <= MAX_WEEKDAY;
}

export enum OccurrenceSuffix {
  First = 1,
  Second,
  Third,
  Fourth,
  Fifth,
}
export function getDayInfo(dateString: Nullable<string>) {
  //date string format is YYYY-MM-DD
  //set the local timezone
  const date = dateString ? new Date(`${dateString}T00:00:00`) : new Date();
  const day = date.getDay();
  const dayOfMonth = date.getDate();
  const weekday = WeekdaysEnum[day];
  const occurrence = Math.ceil(dayOfMonth / 7);
  const occurrenceSuffix = OccurrenceSuffix[occurrence];
  return {
    day,
    dayOfMonth,
    weekday,
    occurrenceSuffix,
    occurrence,
  };
}
